import { useState, useEffect } from 'react';
import { getMeAction } from '../action';
import type { MeApiResponse, MePayload, BackendWorkspace } from '../types';
import { getFullAvatarUrl } from '@/features/settings/components/user-avatar-context/user-avatar-context';

export interface UserWorkspace {
  id: string;
  name: string;
  hasActiveSubscription: boolean;
  hasNeverSubscribed: boolean;
  role?: string;
}

/** Erreur d’accès après GET /users/me (401 / 403) — pour écran dédié CarPulse. */
export interface AuthSessionErrorState {
  status: 401 | 403;
  message: string;
}

export interface User {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  phones: string[];
  avatar: string | null;
  /** ✅ 'ADMIN' = is_superuser, 'USER' = utilisateur standard */
  role: 'USER' | 'ADMIN';
  isActive: boolean;
  isStaff: boolean;
  isSuperuser: boolean;
  permissions: string[];
  workspace: UserWorkspace | null;
  parameters: Record<string, unknown>;
  preferredLanguage: string | null;
}

function mapWorkspace(ws: BackendWorkspace | null): UserWorkspace | null {
  if (!ws) return null;
  return {
    id: ws.id,
    name: ws.name,
    hasActiveSubscription: ws.has_active_subscription,
    hasNeverSubscribed: ws.has_never_subscribed,
    role: ws.role,
  };
}

/** ✅ Admin = is_superuser (is_staff est ignoré pour le rôle) */
function mapRole(payload: MePayload): User['role'] {
  if (payload.is_superuser) return 'ADMIN';
  return 'USER';
}

/** Ne rediriger vers /login que si la session est réellement invalide (401/403), pas sur erreur réseau ou 5xx. */
function isMeResponseAuthFailure(res: MeApiResponse): boolean {
  if (res.success) return false;
  const s = res.status;
  if (s === 401 || s === 403) return true;
  if (res.error === 'Non authentifié.') return true;
  return false;
}

function mapUser(payload: MePayload): User {
  const fullName =
    [payload.first_name, payload.last_name].filter(Boolean).join(' ').trim() ||
    'Utilisateur';

  return {
    id:                payload.id,
    name:              fullName,
    firstName:         payload.first_name  ?? '',
    lastName:          payload.last_name   ?? '',
    email:             payload.email       ?? '',
    phones:            payload.phones      ?? [],
    phone:             payload.phones?.[0] ?? null,
    avatar:            getFullAvatarUrl(payload.avatar_url),
    role:              mapRole(payload),
    isActive:          payload.is_active    ?? false,
    isStaff:           payload.is_staff     ?? false,
    isSuperuser:       payload.is_superuser ?? false,
    permissions:       payload.permissions  ?? [],
    workspace:         mapWorkspace(payload.workspace),
    parameters:        payload.parameters   ?? {},
    preferredLanguage: null,
  };
}

export function useUser() {
  const [user, setUser]       = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<AuthSessionErrorState | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchUser = async () => {
      try {
        setIsLoading(true);
        const response = await getMeAction();

        if (cancelled) return;

        if (response.success && response.payload) {
          setUser(mapUser(response.payload));
          setAuthError(null);
        } else if (!response.success) {
          if (isMeResponseAuthFailure(response)) {
            const status: 401 | 403 =
              response.status === 403 ? 403 : 401;
            setAuthError({
              status,
              message: response.error ?? '',
            });
          } else {
            setAuthError(null);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setAuthError(null);
          console.error('[useUser] Erreur inattendue lors du chargement du profil', e);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchUser();
    return () => { cancelled = true; };
  }, []);

  return { user, isLoading, authError };
}